import {
  SUPPORTED_SMS_FILE_SIZE,
  SUPPORTED_SMS_FILE_TYPES,
} from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { Button, LinearProgress, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTracking } from 'react-components/tracking';
import { loadFile, selectFilesMap } from 'redux/slices/filesSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { FileInput } from 'toolkit/media/FileInput';
import FilePreviewer from 'toolkit/media/file-previewer/FilePreviewer';
import { FileThumbnail } from 'toolkit/media/FileThumbnail';
import { UseMessageAttachmentsResult } from '../hooks/useMessageAttachments.hook';

interface MessageAttachmentsProps {
  attachments: UseMessageAttachmentsResult;
  existingFileIds?: string[];
  isSending: boolean;
}

const MAX_ATTACHMENT_COUNT = 4;

// FlexContainer defaults to `height: 100%`, which fills the dialog body.
const attachButtonRowProps = {
  direction: 'row',
  justifyContent: 'flex-end',
  height: 'auto',
  padding: '8px 0 0',
} as const;

/**
 * Attachment panel for the message dialog: staged uploads, files already on the
 * message, upload progress, and the attach control. Loads existing files into
 * the files slice so their names can be displayed.
 * @param attachments - State and handlers from `useMessageAttachments`.
 * @param existingFileIds - File ids already attached to the message being edited.
 * @param isSending - Whether the message submission is in flight.
 * @returns The attachments panel, or a bare attach button when there are none.
 */
const MessageAttachments = ({
  attachments,
  existingFileIds,
  isSending,
}: MessageAttachmentsProps) => {
  const dispatch = useAppDispatch();
  const filesMap = useAppSelector(selectFilesMap);
  const { generateTrackingProps } = useTracking();
  const {
    selectedFiles,
    setSelectedFiles,
    removeFile,
    removeExistingFile,
    retainedFileIds,
    isUploading,
    uploadProgress,
  } = attachments;

  useEffect(() => {
    existingFileIds?.forEach(fileId => {
      dispatch(loadFile({ id: fileId }));
    });
  }, [existingFileIds, dispatch]);

  const fileInput = (
    <FileInput
      files={selectedFiles}
      onChange={setSelectedFiles}
      maxCount={MAX_ATTACHMENT_COUNT}
      allowedFileTypes={SUPPORTED_SMS_FILE_TYPES}
      maxFileSize={SUPPORTED_SMS_FILE_SIZE}
    />
  );

  if (!selectedFiles.length && !existingFileIds?.length) {
    return (
      <FlexContainer {...attachButtonRowProps}>
        <Button
          variant="text"
          component="label"
          disabled={isUploading}
          {...generateTrackingProps('attach-file-button')}
        >
          Attach File
          {fileInput}
        </Button>
      </FlexContainer>
    );
  }

  return (
    <div className="attachments">
      <h4 className="attachments-title">Attachments</h4>

      {isUploading && (
        <div className="attachments-upload-progress">
          <div className="attachments-upload-progress-header">
            <Typography variant="body2">
              Uploading {selectedFiles.length} file
              {selectedFiles.length > 1 ? 's' : ''}...
            </Typography>
            <Typography variant="body2">
              {Math.round(uploadProgress)}%
            </Typography>
          </div>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </div>
      )}

      <div className="attachments-file-list">
        {retainedFileIds.map(fileId => {
          if (!filesMap[fileId]?.file) {
            return null;
          }

          return (
            <div key={fileId} className="attachments-file-item">
              <div className="attachments-file-thumbnail">
                <FilePreviewer filedId={fileId} />
              </div>
              <Typography variant="body2" className="attachments-file-name">
                {filesMap[fileId].file.name}
              </Typography>
              <Button
                onClick={() => removeExistingFile(fileId)}
                disabled={isSending || isUploading}
                variant="text"
                color="error"
                {...generateTrackingProps('remove-existing-attachment-button')}
              >
                Remove
              </Button>
            </div>
          );
        })}

        {selectedFiles.map(file => (
          <div key={file.name} className="attachments-file-item">
            <div className="attachments-file-thumbnail">
              <FileThumbnail file={file} />
            </div>
            <Typography variant="body2" className="attachments-file-name">
              {file.name}
            </Typography>
            <Button
              onClick={() => removeFile(file)}
              disabled={isSending || isUploading}
              variant="text"
              color="error"
              {...generateTrackingProps('remove-attachment-button')}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>

      <FlexContainer {...attachButtonRowProps}>
        <Button
          variant="outlined"
          component="label"
          disabled={isUploading}
          {...generateTrackingProps('attach-file-button')}
        >
          Attach File
          {fileInput}
        </Button>
      </FlexContainer>
    </div>
  );
};

export default MessageAttachments;
